import { useQuery, useMutation, UseQueryResult } from '@tanstack/react-query';
import { axios } from 'utils/axios';
import { SessionStorageKey } from 'hooks/useSessionStorage';
import * as Sentry from '@sentry/react';

type MarketingModalData = {
  id: string;
  campaign: string;
  header: string;
  subheader: string;
  description: string;
  redirectUrl: string;
  ctaLabel: string;
  isActive: boolean;
};

type CaseMeModalDataResponse = {
  modal: MarketingModalData;
  sessionId: string;
};

const getCaseMeModalData = async (params: { id?: string }): Promise<MarketingModalData | null> => {
  const { id } = params;
  if (!id) return null;

  const storedSessionId = sessionStorage.getItem(SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID);

  const config = storedSessionId ? { params: { aid: storedSessionId } } : {};

  const { data } = await axios.get<CaseMeModalDataResponse>(
    `/marketing-modal/case-me/${id}`,
    config
  );

  if (data.sessionId) {
    sessionStorage.setItem(SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID, data.sessionId);
  }

  return data.modal;
};

/**
 * Tracks when a user clicks on the CTA button in a marketing modal
 */
const trackCtaClick = async (): Promise<void> => {
  const sessionId = sessionStorage.getItem(SessionStorageKey.MARKETING_MODAL_ANONYMOUS_ID);

  if (!sessionId) {
    return;
  }

  try {
    await axios.post('/marketing-modal/track-cta', {
      sessionId,
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const useCaseMe = (params: {
  id?: string;
}): UseQueryResult<MarketingModalData | null, unknown> & {
  trackCtaClick: () => void;
} => {
  const { id } = params;
  const query = useQuery({
    queryKey: ['caseMe', id],
    queryFn: () => getCaseMeModalData({ id }),
    enabled: !!id,
  });

  const ctaClickMutation = useMutation({
    mutationFn: trackCtaClick,
  });

  return {
    ...query,
    trackCtaClick: () => {
      ctaClickMutation.mutate();
    },
  };
};
